import ReactGA from 'react-ga4';

class AnalyticsService {
  constructor() {
    this.GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
    this.isInitialized = false;
    this.userId = null;
    this.campaignParams = this.getCampaignParams();
  }

  // Extract UTM and other campaign parameters from URL
  getCampaignParams() {
    const params = new URLSearchParams(window.location.search);
    const campaignParams = {};

    // UTM parameters
    const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    utmParams.forEach(param => {
      const value = params.get(param);
      if (value) {
        campaignParams[param] = value;
        // Also store in sessionStorage for persistence across page navigations
        sessionStorage.setItem(param, value);
      } else {
        // Try to get from sessionStorage if not in current URL
        const storedValue = sessionStorage.getItem(param);
        if (storedValue) {
          campaignParams[param] = storedValue;
        }
      }
    });

    // Other common tracking parameters
    const otherParams = ['ref', 'referrer', 'source'];
    otherParams.forEach(param => {
      const value = params.get(param);
      if (value) {
        campaignParams[param] = value;
        sessionStorage.setItem(param, value);
      } else {
        const storedValue = sessionStorage.getItem(param);
        if (storedValue) {
          campaignParams[param] = storedValue;
        }
      }
    });

    return campaignParams;
  }

  // Check if running in Cypress test environment
  isCypressTest() {
    return typeof window !== 'undefined' && window.Cypress;
  }

  initialize() {
    // Skip analytics initialization for Cypress tests
    if (this.isCypressTest()) {
      console.log('📊 Google Analytics disabled for Cypress tests');
      return;
    }

    if (!this.GA_MEASUREMENT_ID || this.isInitialized) {
      if (process.env.NODE_ENV === 'development' && !this.GA_MEASUREMENT_ID) {
        console.warn('⚠️ Google Analytics measurement ID not configured. Set REACT_APP_GA_MEASUREMENT_ID to enable analytics.');
      }
      return;
    }

    try {
      ReactGA.initialize(this.GA_MEASUREMENT_ID, {
        gaOptions: {
          anonymizeIp: true,
          cookieFlags: 'SameSite=None; Secure'
        }
      });

      this.isInitialized = true;
      console.log('📊 Google Analytics initialized');

      // Send initial page view
      this.trackPageView(window.location.pathname + window.location.search);
    } catch (error) {
      console.error('Failed to initialize Google Analytics:', error);
    }
  }

  // Set user ID after successful login
  setUserId(userId, userProperties = {}) {
    if (!this.isInitialized) {
      console.warn('⚠️ Analytics not initialized, cannot set user ID');
      return;
    }

    this.userId = userId;

    // Set user ID for all future events
    ReactGA.gtag('config', this.GA_MEASUREMENT_ID, {
      user_id: userId,
      send_page_view: false // Prevent duplicate page views
    });

    // Set user properties for enhanced tracking
    ReactGA.gtag('set', 'user_properties', {
      subscription_tier: userProperties.subscriptionTier || 'free',
      user_type: userProperties.userType || 'standard',
      signup_date: userProperties.signupDate,
      payment_status: userProperties.paymentStatus
    });

    // Also set as custom dimensions for better segmentation
    ReactGA.set({
      dimension1: userProperties.subscriptionTier || 'free',
      dimension2: userProperties.userType || 'standard',
      dimension3: userProperties.paymentStatus || 'inactive'
    });

    console.log(`📊 User ID set: ${userId}`, {
      tier: userProperties.subscriptionTier,
      type: userProperties.userType,
      paymentStatus: userProperties.paymentStatus
    });
  }

  // Clear user ID on logout
  clearUserId() {
    if (!this.isInitialized) return;

    this.userId = null;

    ReactGA.gtag('config', this.GA_MEASUREMENT_ID, {
      user_id: null
    });
  }

  // Track page views
  trackPageView(page, title) {
    if (!this.isInitialized) return;

    const pageTitle = title || document.title;

    ReactGA.send({
      hitType: 'pageview',
      page,
      title: pageTitle,
      user_id: this.userId,
      ...this.campaignParams
    });

    // Also send via gtag for redundancy
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: page,
        page_title: pageTitle,
        page_location: window.location.href,
        user_id: this.userId,
        ...this.campaignParams
      });
    }

    // Log page views in development
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 Page view:', page, pageTitle, this.campaignParams);
    }
  }

  // Track custom events
  trackEvent(eventName, parameters = {}) {
    if (!this.isInitialized) return;

    // Add user context and campaign params to all events
    const eventParams = {
      ...parameters,
      user_id: this.userId,
      timestamp: new Date().toISOString(),
      ...this.campaignParams
    };

    ReactGA.event(eventName, eventParams);
  }

  // Track transcription events
  trackTranscription(details) {
    this.trackEvent('transcription_completed', {
      category: 'transcription',
      method: details.method || 'whisper',
      file_size_kb: Math.round((details.fileSize || 0) / 1024),
      duration_seconds: details.duration,
      subscription_tier: details.subscriptionTier,
      success: details.success,
      value: details.duration // Duration in seconds as value
    });
  }

  // Track subscription events
  trackSubscription(action, tier, value) {
    this.trackEvent(`subscription_${action}`, {
      category: 'subscription',
      subscription_tier: tier,
      value: value || this.getTierValue(tier),
      currency: 'USD'
    });
  }

  // Track user authentication events
  trackLogin(method, subscriptionTier) {
    this.trackEvent('login', {
      category: 'authentication',
      method: method || 'email',
      subscription_tier: subscriptionTier || 'free'
    });
  }

  trackLogout() {
    this.trackEvent('logout', {
      category: 'authentication'
    });
  }

  trackSignup(method) {
    this.trackEvent('sign_up', {
      category: 'authentication',
      method: method || 'email'
    });
  }

  // Track file operations
  trackFileUpload(fileSize, fileType) {
    this.trackEvent('file_upload', {
      category: 'file_operations',
      file_size_kb: Math.round(fileSize / 1024),
      file_type: fileType
    });
  }

  trackFileDownload(format) {
    this.trackEvent('file_download', {
      category: 'file_operations',
      format: format
    });
  }

  // Track share events
  trackShare(includeAudio) {
    this.trackEvent('transcript_share', {
      category: 'sharing',
      include_audio: includeAudio
    });
  }

  // Track user engagement
  trackFeatureUse(featureName, details = {}) {
    this.trackEvent('feature_use', {
      category: 'engagement',
      feature_name: featureName,
      ...details
    });
  }

  // Track errors
  trackError(errorType, errorMessage) {
    this.trackEvent('error', {
      category: 'errors',
      error_type: errorType,
      error_message: errorMessage
    });
  }

  // Track performance metrics
  trackPerformance(metric, value, unit) {
    this.trackEvent('performance_metric', {
      category: 'performance',
      metric_name: metric,
      value: value,
      unit: unit
    });
  }

  // Helper to get monetary value for tiers
  getTierValue(tier) {
    const values = {
      free: 0,
      basic: 9,
      premium: 29,
      enterprise: 99
    };
    return values[tier] || 0;
  }

  // Track timing events
  trackTiming(category, variable, value, label) {
    if (!this.isInitialized) return;

    ReactGA.gtag('event', 'timing_complete', {
      name: variable,
      value: value,
      event_category: category,
      event_label: label
    });
  }

  // Check if analytics should be initialized (GDPR compliance)
  shouldInitialize() {
    // Check for user consent
    const consent = localStorage.getItem('analytics_consent');
    return consent === 'accepted';
  }

  // Set consent and initialize if needed
  setConsent(accepted) {
    localStorage.setItem('analytics_consent', accepted ? 'accepted' : 'declined');

    // Set cookie to remember consent for 1 year
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);
    document.cookie = `analytics_consent=${accepted ? 'accepted' : 'declined'}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;

    if (accepted && !this.isInitialized) {
      this.initialize();
    }
  }

  // Check if user has given consent
  hasConsent() {
    return localStorage.getItem('analytics_consent') === 'accepted';
  }
}

export default new AnalyticsService();